'use es6';

import { useTheme } from 'foundations-theming/utils';
import { useCallback, useEffect, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIButton from 'UIComponents/button/UIButton';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UISlider from 'UIComponents/input/UISlider';
import { ConversionAssetPosition, ConversionAssetTypes } from '../../constants/video';
import CustomTimeInput from './CustomTimeInput';
import VideoPlayer from './VideoPlayer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONVERSION_ASSET_DEFAULT_OPTIONS = {
  position: ConversionAssetPosition.PRE,
  overlay_delay_seconds: 0
};
const RECOMMENDED_POSITION_VALUE = 'RECOMMENDED';
const RECOMMENDED_OVERLAY_DELAY_PERCENTAGE = 0.15;
const getPositionOptions = () => {
  const PRE_POSITION_OPTION = {
    value: ConversionAssetPosition.PRE,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.pre')
  };
  const POST_POSITION_OPTION = {
    value: ConversionAssetPosition.POST,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.post')
  };
  const CUSTOM_POSITION_OPTION = {
    value: ConversionAssetPosition.CUSTOM,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.custom')
  };
  const RECOMMENDED_POSITION_OPTION = {
    value: RECOMMENDED_POSITION_VALUE,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.recommended')
  };
  return [RECOMMENDED_POSITION_OPTION, PRE_POSITION_OPTION, POST_POSITION_OPTION, CUSTOM_POSITION_OPTION];
};
const getPreventSkipOptions = type => {
  if (type === ConversionAssetTypes.FORM) {
    return [{
      value: false,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.canSkipForm')
    }, {
      value: true,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.preventSkipForm')
    }];
  }
  if (type === ConversionAssetTypes.CTA) {
    return [{
      value: false,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.canSkipCta')
    }, {
      value: true,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.preventSkipCta')
    }];
  }
  return [];
};
const ConversionAssetOptionsPanel = ({
  conversionAsset,
  video,
  onSubmit,
  onClose,
  trackVideoInteraction = () => {}
}) => {
  const trellisTheme = useTheme();
  const [conversionAssetState, setConversionAsset] = useState(Object.assign({}, CONVERSION_ASSET_DEFAULT_OPTIONS, conversionAsset));
  const [currentTime, setCurrentTime] = useState(conversionAsset.overlay_delay_seconds || 0);
  useEffect(() => {
    trackVideoInteraction('open-conversion-options');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    id,
    type
  } = conversionAssetState;
  const recommendedPosition = Math.floor(RECOMMENDED_OVERLAY_DELAY_PERCENTAGE * (video.duration / 1000));
  const selectedPositionOption = conversionAssetState.position === ConversionAssetPosition.CUSTOM && recommendedPosition === currentTime ? RECOMMENDED_POSITION_VALUE : conversionAssetState.position;
  const handlePositionChange = useCallback(({
    target: {
      value
    }
  }) => {
    trackVideoInteraction(`select-position-${value.toLowerCase()}`);
    if (value === RECOMMENDED_POSITION_VALUE) {
      value = ConversionAssetPosition.CUSTOM;
      setCurrentTime(recommendedPosition);
    }
    const updatedAsset = Object.assign({}, conversionAssetState, {
      position: value
    });
    setConversionAsset(updatedAsset);
  }, [conversionAssetState, recommendedPosition, trackVideoInteraction]);
  const handlePreventSkipChange = useCallback(({
    target: {
      value
    }
  }) => {
    trackVideoInteraction(`select-overlay-${value ? 'prevent' : 'allow'}-skip`);
    setConversionAsset(Object.assign({}, conversionAssetState, {
      prevent_skip: value
    }));
  }, [conversionAssetState, trackVideoInteraction]);
  const handleSubmit = () => {
    if (conversionAssetState.position === ConversionAssetPosition.CUSTOM) {
      if (currentTime) {
        conversionAssetState.overlay_delay_seconds = currentTime;
      } else {
        conversionAssetState.position = ConversionAssetPosition.PRE;
        delete conversionAssetState.overlay_delay_seconds;
      }
    }
    if (conversionAssetState.overlay_delay_seconds === 0) {
      delete conversionAssetState.overlay_delay_seconds;
    }
    trackVideoInteraction('submit-conversion-options');
    onSubmit(conversionAssetState);
  };
  const handleClose = () => {
    trackVideoInteraction('close-conversion-options');
    onClose();
  };
  return /*#__PURE__*/_jsxs(UIPanel, {
    className: "mce-video-conversion-asset-options",
    width: 600,
    "data-test-id": 'conversion-asset-options-panel',
    children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: handleClose
    }), /*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsx("h2", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.header'
        })
      })
    }), /*#__PURE__*/_jsxs(UIPanelSection, {
      children: [id && video && /*#__PURE__*/_jsxs("div", {
        className: "m-y-4",
        children: [/*#__PURE__*/_jsx(VideoPlayer, {
          video: video,
          portalId: video.portalId,
          conversionAsset: conversionAssetState,
          maxHeight: 300,
          currentTime: currentTime,
          hiddenControls: conversionAssetState.position === ConversionAssetPosition.CUSTOM,
          backgroundColor: "black"
        }), conversionAssetState.position === ConversionAssetPosition.CUSTOM && video.duration && /*#__PURE__*/_jsx("div", {
          className: "p-x-4 p-y-3",
          style: {
            backgroundColor: trellisTheme.color['do-not-use-slinky']
          },
          children: /*#__PURE__*/_jsx(UISlider, {
            min: 0,
            max: Math.floor(video.duration / 1000),
            value: currentTime,
            onChange: e => {
              trackVideoInteraction('change-overlay-delay-via-slider');
              setCurrentTime(e.target.value);
            }
          })
        })]
      }), conversionAssetState.id && /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.position.label'
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          options: getPositionOptions(),
          value: selectedPositionOption,
          onChange: handlePositionChange,
          dropdownClassName: "mce-ignore",
          "data-test-id": "conv-asset-panel-position-select"
        })
      }), conversionAssetState.position === ConversionAssetPosition.CUSTOM && /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.delaySeconds.label'
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.delaySeconds.tooltip'
        }),
        children: /*#__PURE__*/_jsx(CustomTimeInput, {
          value: currentTime,
          video: video,
          onChange: setCurrentTime,
          trackVideoInteraction: trackVideoInteraction
        })
      }), id && /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.preventSkip.label'
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `RichTextLib.video.conversionAssetPanel.fields.preventSkip.help.${type}`
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          options: getPreventSkipOptions(type),
          value: conversionAssetState.prevent_skip || false,
          onChange: handlePreventSkipChange,
          dropdownClassName: "mce-ignore",
          "data-test-id": "conv-asset-panel-prevent-skip-select"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIPanelFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        use: "primary",
        onClick: handleSubmit,
        "data-test-id": "submit-conversion-asset-options",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.footer.done'
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.footer.cancel'
        })
      })]
    })]
  });
};
export default ConversionAssetOptionsPanel;